/**
 * BrandBiblePage — URL-driven, A-I auto-saving brand bible editor.
 *
 * Replaces the prior milestone+tab+modal layout. All chip fields use bespoke editors
 * (ComboboxLocations, ServicesPicker, CompetitorsInput, SmartTagInput) and every section
 * header has an AISuggest pill for inline AI assistance.
 *
 * Existing fields are persisted through BrandBibleUpdate. Contact, social,
 * guardrail, persona, and visual-design fields use existing JSONB columns.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { confirmRemoveGathered, isConfirmedGathered } from '@/components/brand-bible/confirmRemoveGathered';
import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Mail,
  Phone,
  Pin,
  Plus,
  Upload,
  X,
  Zap,
  Loader2,
} from 'lucide-react';

import { useQueryClient } from '@tanstack/react-query';
import { useTenantStore } from '../stores/useTenantStore';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { isElevatedRole } from '@/lib/roles';
import { useBrandBible, brandBibleKeys, useCreateBrandBible, useUpdateBrandBible } from '../hooks/useBrandBible';
import { brandBibleService } from '../services/brandBibleService';

import {
  useAddToPolicy,
  useLinkPolicy,
  useRemoveFromPolicy,
} from '../hooks/useCompetitorTools';
import type { BuyerPersona } from '../types/tenant';

import {
  AISuggest,
  BbFileDropCard,
  ColorChip,
  ComboboxLocations,
  CompetitorsInput,
  FieldReviewChip,
  ChannelRulesPanel,
  NeedsAttentionQueue,
  type AttentionRow,
  OffersPanel,
  PersonaCard,
  ServiceChipDetailRow,
  ServicesPicker,
  SmartTagInput,
  VoiceProfilePanel,
} from '../components/brand-bible';
import { ProofLibraryPanel } from '../components/brand-bible/ProofLibraryPanel';
import { CompliancePanel } from '../components/brand-bible/CompliancePanel';
// restored from fold-in park 55822ae6f for §E Market Notes (recover/bb-e-foldin)

import { SectionAudience } from '@/components/brand-bible/sections/SectionAudience';
import { SectionEssentials } from '@/components/brand-bible/sections/SectionEssentials';
import { bbEntryKindLabel, bbEntryValue } from '../components/brand-bible/bbEntryLabel';
import { CompetitiveSetSection } from '../components/brand-bible/CompetitiveSetSection';
import { VisualIdentityPanel } from '../components/brand-bible/VisualIdentityPanel';
import { useBbExtractions } from '@/hooks/useBbExtractions';
import { VerifyFunnelBar, useBbDropReview, useVerifyNextFunnel } from '@/components/brand-bible/attention';
import { cn } from '@/lib/utils';
import {
  FieldLabel,
  IdentityField,
  TextArea,
  TextField,
} from '@/components/brand-bible/formPrimitives';
import {
  BbDraftCommitProvider,
  CONVERTED_SECTIONS,
  useBbSectionSaveRegistry,
  useDirtyLetters,
  useIsAnySectionDirty,
  useIsSectionRegistered,
  useSectionChangeCount,
  useSectionSaveCompletion,
} from '@/components/brand-bible/save';

import { FEATURE_FLAGS } from '@/config/environment';
import { BrandAccentPreview } from '@/components/brand-bible/BrandAccentPreview';
import { ImageSourceSettings } from './content-preferences/ImageSourceSettings';
import {
  BRAND_BIBLE_SECTIONS,
  BrandBibleSectionPager,
} from '@/components/brand-bible/BrandBibleSectionNavigation';
import {
  sectionVisibilityProps,
  useBrandBibleSection,
} from '@/components/brand-bible/useBrandBibleSection';

export { BrandAccentPreview } from '@/components/brand-bible/BrandAccentPreview';

// Moved to ./brand-bible/draftModel (3,000-line rule); re-exported because tests and the
// section components import these from the page.
export {
  buildBrandBiblePayload,
  buildLegacyOnlyPayload,
  computePriceGapSummary,
  sectionStatus,
} from './brand-bible/draftModel';
import { SectionHeader } from '@/components/brand-bible/SectionHeader';
import { useLegacyBibleInvalidateOnLeave } from './brand-bible/useLegacyBibleInvalidateOnLeave';
import { ChunkVerifyListBody } from '@/components/brand-bible/ChunkVerifyListBody';
import { BrandBibleSubNav } from '@/components/brand-bible/BrandBibleSubNav';
import { useV2DraftSync, type BoundDiscard, type BoundUpsert, type BoundVerify } from './brand-bible/useV2DraftSync';
export type {
  BrandBibleDraft,
  PriceGapSummary,
  SectionStatusV2Context,
} from './brand-bible/draftModel';
export { useLegacyBibleInvalidateOnLeave };

// Brand Bible v2 fold-in (design spec): the wizard's zustand store already
// shapes entries/score exactly to contract Appendix A — reuse it directly
// instead of building a second data layer. EnrichmentDraftsPanel/ScoreChip/
// VerificationRow are the same wizard components, ported in as-is.
import {
  useBrandBibleWizardStore,
  selectSessionDelta,
} from '@/stores/brandBibleWizardStore';
import type { BbEntry, BbTable, IdentityPayload } from '@/features/brand-bible-wizard/types';
import { EnvelopeBadge, ScoreChip } from '@/components/brand-bible-wizard/primitives';
import { useSingleton } from '@/components/brand-bible-wizard/steps/useSingleton';
import { Skeleton } from '@/components/ui/skeleton';
import {
  chunkDraftsForSection,
  sectionForEntry,
  sectionRollups,
  BB_LETTER_LABEL,
  BB_LETTER_TO_SECTION,
  type BbLetter,
  type SectionId,
} from '@/lib/bbSectionMap';
import { EnrichmentDraftsPanel, type DraftRow } from '@/components/brand-bible-wizard/EnrichmentDraftsPanel';
import { GatherBanner } from '@/components/brand-bible-wizard/GatherBanner';
import { QuestionnairePanel } from '@/components/brand-bible-wizard/QuestionnairePanel';
import { sourceKey, useBbSources } from '@/hooks/useBbSources';
import {
  SUGG_WORDS_AVOID,
  SUGG_SERVICES_NOT,
  type SocialLinks,
  EMPTY_DRAFT,
  parseColors,
  stringifyColors,
  fromBrandBible,
  LEGACY_ONLY_SCALAR_KEYS,
  LEGACY_SCORED,
  BrandBibleDraft,
  buildBrandBiblePayload,
  buildLegacyOnlyPayload,
  computePriceGapSummary,
  SectionStatusV2Context,
  sectionStatus,
} from './brand-bible/draftModel';

// ---------------------------------------------------------------------------
// Section meta
// ---------------------------------------------------------------------------

// Preserve the public export used by the existing section-contract tests while
// keeping the letter↔section relationship owned by bbSectionMap.ts.
export const SECTIONS = BRAND_BIBLE_SECTIONS;


// Shared form primitives moved to components/brand-bible/formPrimitives.tsx so
// section panels can import them without importing this page. Re-exported here
// because existing section components and tests still import them from the
// page; each section's explicit-save conversion PR flips its own imports to
// the formPrimitives module directly.
export { FieldLabel, IdentityField, TextArea, TextField, SectionHeader };


const BrandBiblePage: React.FC = () => {
  const { currentTenant, tenants } = useTenantStore();
  const navigate = useNavigate();
  const tenantWebsite = currentTenant ?? '';
  const tenantInfo = tenants.find((t) => t.website === currentTenant);

  const { data: brandBible, isLoading, isError: flatLoadFailed, refetch: refetchFlat } = useBrandBible(currentTenant);
  const createMutation = useCreateBrandBible();
  const updateMutation = useUpdateBrandBible();

  const { entries: blocklist = [] } = useLinkPolicy(currentTenant, 'never_use');
  const addToPolicy = useAddToPolicy();
  const removeFromPolicy = useRemoveFromPolicy();

  // Item 4 — per-field human review. Confirm/reset is elevated-agency-only.
  const userProfile = useWorkspaceStore((st) => st.userProfile);
  const canReview = isElevatedRole(userProfile?.platform_role);
  const queryClient = useQueryClient();
  const [reviewBusy, setReviewBusy] = useState<string | null>(null);

  // ---- Brand Bible v2 fold-in: the wizard store, reused as-is ----------------
  // Verify = any workspace editor+ (contract §4.3); the server is the real gate,
  // this is the same optimistic check BrandBibleWizardPage used.
  const canVerifyV2 = Boolean(userProfile);
  // N3: the frontend has no workspace-role source, so this is only "signed in". The real editor+
  // gate is the server: GET /gather/eligibility answers 403 to a viewer, which hides the banner.
  const canTryGather = canVerifyV2;
  const initV2 = useBrandBibleWizardStore((s) => s.init);
  const v2Loading = useBrandBibleWizardStore((s) => s.loading);
  const v2Saving = useBrandBibleWizardStore((s) => s.saving);
  const v2Score = useBrandBibleWizardStore((s) => s.score);
  const v2Entries = useBrandBibleWizardStore((s) => s.entries);
  const v2Delta = useBrandBibleWizardStore(selectSessionDelta);
  const upsertEntryRaw = useBrandBibleWizardStore((s) => s.upsertEntry);
  const verifyEntryRaw = useBrandBibleWizardStore((s) => s.verifyEntry);
  // restored from fold-in park 55822ae6f for §E Market Notes (recover/bb-e-foldin)
  const unverifyEntryRaw = useBrandBibleWizardStore((s) => s.unverifyEntry);
  const discardEntryRaw = useBrandBibleWizardStore((s) => s.discardEntry);
  // The store is global and flips on init(): every write from this page names ITS tenant, and the
  // store refuses one that no longer matches (tenant-switch bug: a stale closure wrote to the new client).
  const pageTenant = currentTenant ?? '';
  const upsertV2: BoundUpsert = (table, payload, id) => upsertEntryRaw(table, payload, id, pageTenant);
  const verifyV2: BoundVerify = (table, id, approve) => verifyEntryRaw(table, id, approve, pageTenant);
  const unverifyV2 = (table: BbTable, id: string) => unverifyEntryRaw(table, id, pageTenant);
  const discardV2: BoundDiscard = (table, id) => discardEntryRaw(table, id, pageTenant);

  // §A spec-complete Identity Card (design d4f317d6, frame "A · Identity Card").
  // identity is the bb_identity singleton row — legal_name/website duplicate the
  // flat draft fields below them (BB-DRAFTS-UX-01's businessNameDup precedent) so
  // existing consumers of draft.business_name/draft.website keep working; every
  // other typed field (dba/founded_year/team_size/owner/booking_url/socials) has
  // no flat home and lives only on this row.
  const identity = useSingleton('identity', currentTenant ?? '');
  const identityIsDraft = Boolean(
    identity.entry && identity.entry.source === 'discovered' && !identity.entry.verified_by,
  );
  // R19: while a web gather runs, nothing is written (autosave and explicit save are held);
  // the held edit is saved by the autosave effect once the flag drops. The ref lets the
  // debounced timer and saveDraftNow see the live value instead of a stale closure.
  const gatherRunning = useBrandBibleWizardStore((s) => s.gatherRunning);
  const gatherRunningRef = useRef(gatherRunning);
  gatherRunningRef.current = gatherRunning;
  // The draft as it was hydrated (and as last pushed): pushDraftToV2 sends only what differs.
  const [hydratedDraft, setHydratedDraft] = useState<BrandBibleDraft | null>(null);
  // §7.7: tone_of_writing projects FROM bb_voice_profile.voice_summary — for a
  // v2-active tenant the legacy "Tone of voice" box is a decoy (edits can't
  // save: guarded PUT + §7.6's 422, and the projection overwrites the flat
  // column from this value on every mutation). Read here so that field can
  // render as a read-only mirror instead of pretending to be editable.
  const voiceProfileForToneMirror = useSingleton('voice_profile', currentTenant ?? '');
  // §7.5 fault fix / contract §7.3: bb_identity row existing IS the v2-activation
  // predicate. Once true, the legacy flat-row write endpoints 409 server-side —
  // saveDraftNow/the autosave effect below must stop calling them and push §A
  // fields through v2 (identity.save / bb_locations) instead.
  const isV2Active = Boolean(identity.entry);
  // Walk finding 2026-08-17 (§F probe + Carl's toast screenshot): on page load
  // the debounced autosave raced the v2 init fetch — isV2Active read false
  // while bb_identity was still loading, the legacy PUT fired, and the §7.3
  // gate 409'd it ("Failed to update brand bible" toast on EVERY load of a
  // v2-active tenant, and spuriously during otherwise-successful section
  // saves). `loadedTenantId` is the store's own settled marker (Appendix A.7:
  // the tenant whose entries are CURRENTLY valid), so v2-activity is only
  // trustworthy once it matches the current tenant — for v1 tenants init also
  // completes (empty) and sets it, so the gate opens for both kinds. Also
  // blocks cross-tenant saves mid-switch for free.
  const v2LoadedTenantId = useBrandBibleWizardStore((s) => s.loadedTenantId);
  const v2ActivitySettled = Boolean(currentTenant) && v2LoadedTenantId === currentTenant;
  // Appendix A.5a forensic finding: the ORIGINAL twitter loss was silent
  // because every bare identity.save() call site had no .catch() — a failed
  // write vanished with no toast and no retry. Every §A identity.save() call
  // must go through this so a failure is loud, never silent.
  const identitySaveErrorToast = (field: string) => (e: unknown) => {
    message.error(`Failed to save ${field} — ${e instanceof Error ? e.message : 'request failed'}`);
  };

  // Appendix A.6 point 3 (frontend half of the create-race fix): single-flight
  // bb_identity's first write, and let the debounced autosave effect below
  // know a v2 create has started even before the row is visible in the
  // store (closing the legacy-PUT-vs-projection contention window §7.5 was
  // built around). Tracked via refs, not `identity.entry?.id` from the
  // render closure — that closure is stale inside a promise continuation
  // (a store update doesn't retroactively refresh an already-captured
  // callback), which would otherwise reopen exactly the race being closed.
  const identityRowIdRef = useRef<string | null>(null);
  const identityCreateInFlightRef = useRef<Promise<unknown> | null>(null);
  const identityQueuedPatchRef = useRef<Partial<IdentityPayload> | null>(null);
  const [v2CreateInitiated, setV2CreateInitiated] = useState(false);
  // Which tenant's init() this page has requested, and which tenant's flat row the draft is hydrated for:
  // both drive the loading gate below (state, so a change re-renders; refs are read by the write guards).
  const [v2InitFor, setV2InitFor] = useState<string | null>(null);
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);
  const [v2InitFailedFor, setV2InitFailedFor] = useState<string | null>(null);
  const storeTenantId = useBrandBibleWizardStore((s) => s.tenantId);
  useEffect(() => {
    if (identity.entry?.id) identityRowIdRef.current = identity.entry.id;
  }, [identity.entry?.id]);

  const saveIdentity = (patch: Partial<IdentityPayload>): Promise<unknown> => {
    // Warm path: row already exists — an ordinary PATCH by id never races a
    // create, so no single-flighting needed.
    if (identityRowIdRef.current) {
      return upsertV2('identity', patch, identityRowIdRef.current);
    }
    // Cold path, a create is already in flight: merge into the queued
    // follow-up and ride the SAME promise — never fire a second create.
    if (identityCreateInFlightRef.current) {
      identityQueuedPatchRef.current = { ...identityQueuedPatchRef.current, ...patch };
      return identityCreateInFlightRef.current;
    }
    // Cold path, nothing in flight yet: this call becomes the create.
    // S1: the refs belong to the tenant this create STARTED for; a create that resolves after a
    // switch must not write its row id into the new tenant's refs.
    const forTenant = currentTenant;
    const stillThisTenant = () => useBrandBibleWizardStore.getState().tenantId === forTenant;
    setV2CreateInitiated(true);
    const createPromise = identity
      .save(patch)
      .then((created) => {
        if (!stillThisTenant()) return created;
        identityCreateInFlightRef.current = null;
        if (created?.id) identityRowIdRef.current = created.id;
        const queued = identityQueuedPatchRef.current;
        identityQueuedPatchRef.current = null;
        if (queued && created?.id) {
          void upsertV2('identity', queued, created.id).catch(identitySaveErrorToast('identity'));
        }
        return created;
      })
      .catch(async (e: unknown) => {
        if (!stillThisTenant()) return null;
        identityCreateInFlightRef.current = null;
        const queued = identityQueuedPatchRef.current;
        identityQueuedPatchRef.current = null;
        // Reconcile-and-repush: a client timeout does not mean the write was
        // lost server-side. Refetch before declaring the edit gone — an
        // operator's first save must never silently vanish (the exact
        // failure mode the §A.5a forensics traced back to this class).
        // ...and only for the tenant the store is still on: re-initing a previous tenant would re-point the page.
        if (currentTenant && useBrandBibleWizardStore.getState().tenantId === currentTenant) {
          try {
            await initV2(currentTenant);
            const fresh = useBrandBibleWizardStore.getState().entries.identity ?? [];
            const reconciled = fresh.find(
              (row) => row.location_scope === 'all' && row.deleted_at === null,
            );
            if (reconciled?.id) {
              identityRowIdRef.current = reconciled.id;
              const merged = { ...patch, ...queued };
              await upsertV2('identity', merged, reconciled.id).catch(
                identitySaveErrorToast('identity (reapplied after timeout)'),
              );
              message.warning('That save took longer than expected — your change was reapplied.');
              return null;
            }
          } catch {
            // reconcile itself failed too — fall through to the loud error
          }
        }
        identitySaveErrorToast('identity')(e);
        return null;
      });
    identityCreateInFlightRef.current = createPromise;
    return createPromise;
  };

  const verifyIdentity = () => {
    if (!identity.entry) return;
    void verifyV2('identity', identity.entry.id).catch((e: unknown) =>
      message.error(`Verify failed — ${e instanceof Error ? e.message : 'request failed'}`),
    );
  };
  // §7.6 client discipline (contract Appendix A.5/A.5a): owner is a
  // JSONB_MERGE_COLUMNS column server-side — send ONLY the changed subkeys,
  // never a `{...current, ...patch}` spread. A whole-map spread built from a
  // possibly-stale local copy is exactly the class of bug A.5a's forensics
  // were chasing (a stale spread can silently resurrect or drop a sibling
  // key); the server now merges, so the client no longer needs to carry the
  // rest of the map at all.
  const saveOwner = (patch: Partial<NonNullable<IdentityPayload['owner']>>) => {
    void saveIdentity({ owner: patch }).catch(identitySaveErrorToast('owner'));
  };
  const [socialDraft, setSocialDraft] = useState('');
  const socialsMap = identity.entry?.socials ?? {};
  const addSocial = () => {
    const raw = socialDraft.trim();
    if (!raw) return;
    const sep = raw.indexOf(':');
    const platform = (sep > -1 ? raw.slice(0, sep) : raw).trim();
    const url = (sep > -1 ? raw.slice(sep + 1) : '').trim();
    if (!platform) return;
    // Only the changed key — the server merges (Appendix A.5), never a
    // whole-map spread of possibly-stale local state.
    void saveIdentity({ socials: { [platform]: url || platform } }).catch(
      identitySaveErrorToast('social link'),
    );
    setSocialDraft('');
  };
  const removeSocial = (platform: string) => {
    // null deletes a key server-side (Appendix A.5, RFC-7396 style) — sending
    // the whole map minus one key was the old (buggy) way to express this.
    void saveIdentity({ socials: { [platform]: null } }).catch(
      identitySaveErrorToast('social link removal'),
    );
  };

  // Tenant switch: drop EVERYTHING that belonged to the previous tenant before anything of the new one
  // is shown or written (declared before the init and hydration effects, so effects run reset, then init/hydrate).
  useEffect(() => {
    setDraft(EMPTY_DRAFT);
    setHydratedDraft(null);
    setHydratedFor(null);
    setV2InitFor(null);
    setV2InitFailedFor(null);
    hydratedTenantRef.current = null;
    lastSavedHashRef.current = '';
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    identityRowIdRef.current = null;
    identityCreateInFlightRef.current = null;
    identityQueuedPatchRef.current = null;
    setV2CreateInitiated(false);
  }, [currentTenant]);
  useEffect(() => {
    // Appendix A.7 test run surfaced this: a bare `void` call on a rejecting
    // promise is an unhandled rejection (same class of gap the A.5a
    // forensics found on every bare identity.save() call site). A load
    // failure here isn't user-actionable directly (the diff-syncs already
    // surface their own "could not load" toast when THEY need the store and
    // it isn't ready), so this just needs to not be silently unhandled.
    if (currentTenant) {
      setV2InitFor(currentTenant);
      const forTenant = currentTenant;
      void initV2(forTenant).catch((e: unknown) => {
        console.error('Brand Bible v2 init failed', e);
        // Only a failure of the tenant the store is still on counts (a stale one is ignored).
        if (useBrandBibleWizardStore.getState().tenantId === forTenant) setV2InitFailedFor(forTenant);
      });
    }
  }, [currentTenant, initV2]);
  useLegacyBibleInvalidateOnLeave(currentTenant);

  // BB-FILE-DROP-01, slice B-FE: file-provenance chips for the queue below.
  const { data: v2Provenance } = useBbExtractions(
    FEATURE_FLAGS.brandBibleFileIngestion ? currentTenant : null,
  );

  // Per-section points, rolled up from the pinned score (contract Appendix A).
  // null for tenants not yet on v2 — those keep the legacy filled-field checks.
  const v2Rollups = useMemo(
    () => (v2Score ? sectionRollups(v2Score.components) : null),
    [v2Score],
  );

  const v2ServicesEntries = useMemo(
    () => (v2Entries.services ?? []).filter((e) => e.deleted_at === null),
    [v2Entries.services],
  );
  const findServiceEntry = (name: string): BbEntry<'services'> | null =>
    v2ServicesEntries.find((e) => (e.name ?? '').trim().toLowerCase() === name.trim().toLowerCase()) ?? null;

  // §C gap 4b: same table, needed only for the C1 core-offers check (guarantee/
  // dispatch fee/financing) — OffersPanel keeps its own read of this table for
  // rendering, this is a second read purely for the rail-truth gate below.
  const v2OffersEntries = useMemo(
    () => (v2Entries.offers ?? []).filter((e) => e.deleted_at === null),
    [v2Entries.offers],
  );

  // §C gap 4b: the v2 context sectionStatus needs to tell the truth about §C —
  // without it, the rail can only see the two legacy flat-row fields (see
  // sectionStatus). Doesn't depend on `draft`, so it's safe to build here,
  // ahead of the `draft` state declaration below.
  const v2ServiceSectionContext = useMemo<SectionStatusV2Context>(
    () => ({ services: v2ServicesEntries, offers: v2OffersEntries }),
    [v2ServicesEntries, v2OffersEntries],
  );

  // §7.5: address's v2 home is the tenant's primary bb_locations row
  // (location_key='primary', created on first save — same composer the
  // backend projection reads for contact_info.address).
  const v2LocationsEntries = useMemo(
    () => (v2Entries.locations ?? []).filter((e) => e.deleted_at === null),
    [v2Entries.locations],
  );
  // restored from fold-in park 55822ae6f for §E Market Notes (recover/bb-e-foldin)
  // §E verify list — bb_chunks rows scoped to §E AND the kinds §E owns, so a
  // chunk mis-tagged into E some other way never shows up as if this were a
  // general-purpose viewer.
  const v2ChunksEEntries = useMemo(
    () =>
      (v2Entries.chunks ?? []).filter(
        (e) =>
          e.deleted_at === null &&
          e.section === 'E' &&
          // R61 S2: market_narrative and area_note are what the client questionnaire drafts here; they
          // need a Confirm control too, or they would sit unseen forever.
          (e.kind === 'icp_note' ||
            e.kind === 'seasonality_note' ||
            e.kind === 'market_narrative' ||
            e.kind === 'area_note'),
      ),
    [v2Entries.chunks],
  );
  const primaryLocation = v2LocationsEntries.find((e) => e.location_key === 'primary') ?? null;
  const {
    savePrimaryLocation,
    syncServiceLocationsV2,
    syncServicesOfferedV2,
    pushDraftToV2,
    pushLegacyOnlyFieldsToV2,
  } = useV2DraftSync({
    currentTenant,
    tenantWebsite,
    identity,
    primaryLocation,
    hydratedDraft,
    saveIdentity,
    identitySaveErrorToast,
    upsertV2,
    discardV2,
  });

  // Rows a human should look at: unverified-and-not-a-draft (source !== 'discovered')
  // vs. enrichment drafts (source === 'discovered' && unverified) — same distinction
  // the wizard's selectDrafts() makes, generalized across every wizard-v1 table.
  const { toVerifyRows, draftRows } = useMemo(() => {
    const toVerify: AttentionRow[] = [];
    const drafts: AttentionRow[] = [];
    (Object.entries(v2Entries) as [BbTable, BbEntry[]][]).forEach(([table, list]) => {
      (list ?? [])
        .filter((e) => e.deleted_at === null && e.verified_by === null)
        .forEach((entry) => {
          (entry.source === 'discovered' ? drafts : toVerify).push({ table, entry });
        });
    });
    return { toVerifyRows: toVerify, draftRows: drafts };
  }, [v2Entries]);

  const dropReview = useBbDropReview({
    toVerifyRows,
    draftRows,
    provenance: v2Provenance,
  });
  const funnel = useVerifyNextFunnel(dropReview.dropQueue);
  const pendingDropStart = useRef(false);

  useEffect(() => {
    if (!pendingDropStart.current) return;
    if (dropReview.dropQueue.length === 0) return;
    if (funnel.phase !== 'idle') {
      pendingDropStart.current = false;
      return;
    }
    pendingDropStart.current = false;
    funnel.start();
  }, [dropReview.dropQueue, funnel]);

  const startDropReview = useCallback(
    (fileIds?: string[]) => {
      dropReview.activateFilter(fileIds);
      pendingDropStart.current = true;
      if (!fileIds && dropReview.dropQueue.length > 0 && funnel.phase === 'idle') {
        pendingDropStart.current = false;
        funnel.start();
      }
    },
    [dropReview, funnel],
  );

  // The rail's "N need attention" pill is one lump number; this splits it per
  // section so you can see the queue is concentrated in (say) Proof rather than
  // spread evenly. Rows whose table maps nowhere are simply not counted.
  const v2Attention = useMemo(() => {
    const counts = {} as Record<SectionId, number>;
    [...toVerifyRows, ...draftRows].forEach(({ table, entry }) => {
      const section = sectionForEntry(table, entry);
      if (section) counts[section] = (counts[section] ?? 0) + 1;
    });
    return counts;
  }, [toVerifyRows, draftRows]);

  const [draft, setDraft] = useState<BrandBibleDraft>(EMPTY_DRAFT);

  // §C gap 4a: how many services_offered chips still lack a price policy —
  // feeds the aggregate nudge line under ServicesPicker.
  const servicePriceGap = useMemo(
    () => computePriceGapSummary(draft.services_offered, v2ServicesEntries),
    [draft.services_offered, v2ServicesEntries],
  );

  // 2a (BB-DRAFTS-UX-01): a draft whose value duplicates a filled form field is not a
  // row — the field itself carries a quiet "found on site · Verify" chip instead.
  // Declared before `draftRowsForTables` (below), which reads it on every render —
  // TDZ requires the const to exist before that first call, not just before its
  // own later re-reads (§A Identity Card hotfix; this ordering broke once already).
  const businessNameDup = useMemo(() => {
    const normEq = (a?: string | null, b?: string | null) =>
      Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase());
    return (
      draftRows.find(
        (r) => r.table === 'identity' && normEq((r.entry as BbEntry<'identity'>).legal_name, draft.business_name),
      ) ?? null
    );
  }, [draftRows, draft.business_name]);

  // Where the web gather found each draft (link + quote); an empty map when the endpoint is down.
  const bbSources = useBbSources(currentTenant ?? null).data;

  // Pending identity values (R31): web-found phone/email/socials/... waiting for a person.
  // They live on the identity row's pending_fields, not as rows, so they become kind 'field' rows.
  const pendingFieldRows: DraftRow[] = identity.entry
    ? Object.entries(identity.entry.pending_fields ?? {}).map(([field, pending]) => ({
        kind: 'field' as const,
        field,
        table: 'identity' as BbTable,
        entry: identity.entry as BbEntry,
        label: field.startsWith('socials.') ? `${field.slice('socials.'.length)} link` : field.replace(/_/g, ' '),
        value: pending.value,
        sourceLabel: `site · ${currentTenant ?? 'unknown'}`,
        sources: bbSources?.get(sourceKey('identity', (identity.entry as BbEntry).id, field)),
      }))
    : [];

  const draftRowsForTables = (tables: BbTable[]): DraftRow[] => [
    ...draftRows
      .filter((r) => tables.includes(r.table))
      .filter((r) => r.entry.id !== businessNameDup?.entry.id)
      .map((r) => ({
        table: r.table,
        entry: r.entry,
        label: bbEntryKindLabel(r.table, r.entry),
        value: bbEntryValue(r.table, r.entry),
        sourceLabel: `site · ${currentTenant ?? 'unknown'}`,
        sources: bbSources?.get(sourceKey(r.table, r.entry.id)),
      })),
    ...(tables.includes('identity') ? pendingFieldRows : []),
  ];

  // §B wall — chunk-shaped drafts ONLY (do_say/never_say/golden/rejected have no
  // single field to carry a chip). chunkDraftsForSection routes by each entry's
  // OWN `section` via sectionForEntry, NOT a static ['chunks'] bucket — a static
  // bucket pulls E/F/etc. chunks into Voice (BB-DRAFTS-BUCKETING). voice_profile
  // is deliberately excluded: its draft surfaces field-level inside VoiceProfilePanel.
  const voiceChunkDrafts = chunkDraftsForSection(draftRowsForTables(['chunks']), 'voice');

  const [selectedServiceChip, setSelectedServiceChip] = useState<string | null>(null);

  const runReview = async (
    action: 'confirm' | 'reset',
    fieldKey: string,
    valueAsSeen?: unknown,
  ) => {
    if (!currentTenant) return;
    setReviewBusy(fieldKey);
    try {
      if (action === 'confirm') {
        await brandBibleService.confirmField(currentTenant, fieldKey, valueAsSeen);
        message.success('Field confirmed');
      } else {
        await brandBibleService.resetField(currentTenant, fieldKey);
        message.success('Field returned to needs review');
      }
      queryClient.invalidateQueries({ queryKey: brandBibleKeys.detail(currentTenant) });
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      message.error(
        status === 409
          ? 'This field changed since you viewed it — refresh and review the current value.'
          : status === 403
            ? 'Only agency staff can confirm fields.'
            : 'Could not update review state.',
      );
    } finally {
      setReviewBusy(null);
    }
  };

  // Render the review chip for a critical field. `seenValue` is what the reviewer
  // is looking at right now, sent so the API can reject a stale confirm.
  const reviewChip = (fieldKey: string, seenValue: unknown) => (
    <FieldReviewChip
      review={brandBible?.field_review?.[fieldKey]}
      elevated={canReview}
      busy={reviewBusy === fieldKey}
      onConfirm={() => runReview('confirm', fieldKey, seenValue)}
      onReset={() => runReview('reset', fieldKey)}
    />
  );

  const {
    activeLetter,
    activeSection: active,
    setActiveLetter,
    setActiveSection,
  } = useBrandBibleSection();
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  // §B legacy fields (mission / value proposition / tone of voice / primary CTA)
  // are aliased alongside the new spec-model panel, not replaced by it (Carl
  // addendum) — open by default so they visibly render, not just collapse-to-
  // reach; the toggle exists only to let an operator tidy the canvas, never to
  // gate the fields' own render/save path.
  const [showVoiceLegacy, setShowVoiceLegacy] = useState(true);
  const [usingBrandBible, setUsingBrandBible] = useState(false);

  const sectionRefs = useRef<Record<SectionId, HTMLElement | null>>({
    essentials: null,
    voice: null,
    service: null,
    proof: null,
    audience: null,
    competitors: null,
    guardrails: null,
    visual: null,
    channels: null,
  });
  const scrollerRef = useRef<HTMLElement>(null);
  const pendingSectionFocusRef = useRef<SectionId | null>(null);

  // Hydrate draft once per tenant. Without this guard, every autosave round-trip would
  // invalidate the React Query cache and refetch brandBible/blocklist, retriggering this
  // effect and overwriting any local-only fields (personas, colors, etc.) plus any in-flight
  // typing. Track which tenant we last hydrated and only reset draft when that changes.
  const hydratedTenantRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentTenant) {
      hydratedTenantRef.current = null;
      setHydratedDraft(null);
      return;
    }
    if (hydratedTenantRef.current === currentTenant) return;
    // Wait until the brand bible query has resolved (success or 404=null) before hydrating.
    if (brandBible === undefined) return;
    const blocklistDomains = blocklist.map((b) => b.domain).filter((d): d is string => Boolean(d));
    const nextDraft = fromBrandBible(brandBible ?? null, blocklistDomains);
    setDraft(nextDraft);
    setHydratedDraft(nextDraft);
    setHydratedFor(currentTenant);
    hydratedTenantRef.current = currentTenant;
    lastSavedHashRef.current = '';
  }, [currentTenant, brandBible, blocklist]);

  // Keep the competitors slice of the draft synced with the blocklist table — narrow
  // update so blocklist refetches don't clobber unrelated local fields. Only runs once
  // the draft has been hydrated for this tenant.
  useEffect(() => {
    if (hydratedTenantRef.current !== currentTenant) return;
    const blocklistDomains = blocklist.map((b) => b.domain).filter((d): d is string => Boolean(d));
    setDraft((d) => {
      const same =
        d.competitors.length === blocklistDomains.length &&
        d.competitors.every((c, i) => c === blocklistDomains[i]);
      return same ? d : { ...d, competitors: blocklistDomains };
    });
  }, [blocklist, currentTenant]);

  // Debounced auto-save → mutation. Only fires once required fields are filled
  // (mutation is rejected by backend for empty payloads).
  const saveTimerRef = useRef<number | null>(null);
  const lastSavedHashRef = useRef<string>('');

  // R55 S1: move the push baseline forward only for the fields the push reports as written, with
  // a functional update so a push that finishes late cannot overwrite a newer baseline wholesale.
  const advanceHydrated = (pushed: PromiseSettledResult<Partial<BrandBibleDraft>>, forTenant: string) => {
    if (pushed.status !== 'fulfilled') return;
    // A push of the previous tenant resolving after a switch must not merge into the new tenant's baseline.
    if (hydratedTenantRef.current !== forTenant) return;
    const saved = pushed.value;
    setHydratedDraft((h) => (h ? { ...h, ...saved } : h));
  };

  const saveDraftNow = async (
    nextDraft: BrandBibleDraft,
    includeLetters?: readonly BbLetter[],
  ): Promise<boolean> => {
    if (!currentTenant) return false;
    if (gatherRunningRef.current) {
      message.info('Drafting from the website is running. Your changes will save when it finishes.');
      return false;
    }
    // Never pick a write path before the v2-activity signal has settled —
    // choosing "legacy" off a still-loading store is exactly the load-race
    // 409. Explicit saves this early are rare (the fetch settles in ~1-2s);
    // fail soft and loud rather than fire the wrong endpoint.
    if (
      !v2ActivitySettled ||
      hydratedTenantRef.current !== currentTenant ||
      useBrandBibleWizardStore.getState().loadedTenantId !== currentTenant
    ) {
      message.warning('Brand data is still loading — try saving again in a moment.');
      return false;
    }
    if (
      !nextDraft.business_name?.trim() ||
      nextDraft.description.trim().length < 10 ||
      nextDraft.services_offered.length === 0
    ) {
      message.warning('Fill business name, overview, and services before using the Brand Bible.');
      return false;
    }
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    // ⚠️ The legacy PUT payload is NEVER stripped: BrandBibleUpdate requires
    // company_name/description/services_offered, so removing a converted
    // section's columns 422s the WHOLE document (prod incident 2026-08-17 —
    // every v1-tenant save failed once §A converted). Stripping the PUT also
    // buys nothing: converted sections buffer locally, so the draft — and
    // therefore this payload — only ever carries their last-COMMITTED values.
    // The owner-strip contract is enforced solely on the legacy-fields PATCH
    // (absent-tolerant) inside pushLegacyOnlyFieldsToV2.
    const payload = buildBrandBiblePayload(nextDraft, tenantWebsite || currentTenant);
    const hash = JSON.stringify(payload);
    lastSavedHashRef.current = hash;

    // §7.3/§7.5/§7.6: the legacy PUT/POST 409s server-side for a v2-active
    // tenant — push the §A fields that have v2 homes, plus the legacy_only
    // flat columns via the narrow endpoint, instead of calling it.
    // Appendix A.6 point 3: also suppress once a v2 create is IN FLIGHT
    // (v2CreateInitiated), not just once the row is visible (isV2Active) —
    // otherwise this branch fires the legacy PUT concurrently with the
    // still-resolving create, reopening the legacy-vs-projection flat-row
    // contention window on the same tenant.
    if (isV2Active || v2CreateInitiated) {
      const [pushed] = await Promise.allSettled([
        pushDraftToV2(nextDraft),
        pushLegacyOnlyFieldsToV2(nextDraft, includeLetters),
      ]);
      advanceHydrated(pushed, currentTenant);
      setSavedAt(new Date());
      return true;
    }

    try {
      if (brandBible?.id) {
        await updateMutation.mutateAsync({ id: brandBible.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      setSavedAt(new Date());
      return true;
    } catch {
      lastSavedHashRef.current = '';
      return false;
    }
  };

  useEffect(() => {
    if (!currentTenant) return;
    // Don't autosave until hydration for this tenant has completed — otherwise the first
    // render after a tenant switch would persist EMPTY_DRAFT and wipe the server record.
    if (hydratedTenantRef.current !== currentTenant) return;
    // ...and never before the v2-activity signal settles (see v2ActivitySettled):
    // the load-race legacy PUT → 409 toast came from exactly this window. The
    // effect re-runs when it settles, so nothing is lost, only deferred.
    if (!v2ActivitySettled) return;
    // R19: held while a web gather runs; re-runs when gatherRunning drops (it is a dep below).
    if (gatherRunning) return;
    if (
      !draft.business_name?.trim() ||
      // Backend BrandBibleBase requires description min length 10. Guarding here avoids
      // a stream of 422s while the user is still typing the overview.
      draft.description.trim().length < 10 ||
      draft.services_offered.length === 0
    ) {
      return;
    }
    // Unstripped for the same reason as saveDraftNow: the legacy PUT is a
    // full-document write with required fields — the owner-strip contract
    // lives on the legacy-fields PATCH only. Buffered edits never enter the
    // draft, so this payload cannot leak a converted section's uncommitted
    // values.
    const payload = buildBrandBiblePayload(draft, tenantWebsite);
    const hash = JSON.stringify(payload);
    if (hash === lastSavedHashRef.current) return;

    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    // Capture the tenant at scheduling time — if the user switches tenant before the timer
    // fires, the cleanup function below cancels this save so it can't land cross-tenant.
    const scheduledTenant = currentTenant;
    saveTimerRef.current = window.setTimeout(async () => {
      if (hydratedTenantRef.current !== scheduledTenant) return;
      if (gatherRunningRef.current) return;
      lastSavedHashRef.current = hash;

      // §7.3/§7.5/§7.6: v2-active tenants close the legacy PUT/POST
      // server-side (409) — push the §A fields that have v2 homes plus the
      // legacy_only flat columns via the narrow endpoint instead. Field-level
      // onBlur/onChange handlers already do the §A half per-edit; this
      // debounced pass is the safety net (e.g. a field changed without
      // blurring) and the ONLY path that saves the legacy_only fields, which
      // have no per-field handler of their own.
      // Appendix A.6 point 3: also suppress while a v2 create is IN FLIGHT
      // (v2CreateInitiated) — see saveDraftNow's identical guard for why.
      if (isV2Active || v2CreateInitiated) {
        const [pushed] = await Promise.allSettled([pushDraftToV2(draft), pushLegacyOnlyFieldsToV2(draft)]);
        if (hydratedTenantRef.current === scheduledTenant) {
          advanceHydrated(pushed, scheduledTenant);
          setSavedAt(new Date());
        }
        return;
      }

      try {
        if (brandBible?.id) {
          await updateMutation.mutateAsync({ id: brandBible.id, data: payload });
        } else {
          await createMutation.mutateAsync(payload);
        }
        if (hydratedTenantRef.current === scheduledTenant) {
          setSavedAt(new Date());
        }
      } catch {
        // Hooks already toast on error. Reset the saved-hash so this exact
        // payload is retried on the next edit instead of being suppressed by
        // the dedupe guard above (`if (hash === lastSavedHashRef.current)`).
        // Without this a single failed autosave is silently recorded as saved
        // and the user's change is lost. Matches saveDraftNow's catch. Only
        // reset while still on the tenant this save was scheduled for — a
        // tenant switch already has its own hash, and resetting here would be
        // stomping a different tenant's saved state.
        if (hydratedTenantRef.current === scheduledTenant) {
          lastSavedHashRef.current = '';
        }
      }
    }, 700);

    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
    // Mutation refs are stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, currentTenant, brandBible?.id, tenantWebsite, isV2Active, v2CreateInitiated, v2ActivitySettled, gatherRunning]);

  const focusSection = useCallback((id: SectionId) => {
    const node = sectionRefs.current[id];
    if (!node) return;
    node.focus({ preventScroll: true });
    node.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);

  // The URL is the only active-section truth. A section change resets the
  // canvas to its top; attention/draft jumps wait until React has exposed the
  // target, then retain the existing focus/scroll behavior inside it.
  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
    if (pendingSectionFocusRef.current !== active) return;
    pendingSectionFocusRef.current = null;
    focusSection(active);
  }, [active, focusSection]);

  const jumpToSection = useCallback(
    (id: SectionId) => {
      if (id === active) {
        focusSection(id);
        return;
      }
      pendingSectionFocusRef.current = id;
      setActiveSection(id);
    },
    [active, focusSection, setActiveSection],
  );

  // ------------------------------------------------------------------
  // Explicit-save mount points (bb-phase2-explicit-save-brief-20260810 +
  // bb-save-ux-slate-brief-20260810). All of this is inert until a section
  // joins CONVERTED_SECTIONS and registers: no section registered means no
  // Save button, an always-clean registry, and no beforeunload.
  // ------------------------------------------------------------------
  const saveRegistry = useBbSectionSaveRegistry();
  const dirtyLetters = useDirtyLetters();
  const anySectionDirty = useIsAnySectionDirty();
  // activeLetter comes from useBrandBibleSection() above — URL-driven truth.
  const activeSectionRegistered = useIsSectionRegistered(activeLetter);
  const activeSectionChanges = useSectionChangeCount(activeLetter);
  const [sectionSaving, setSectionSaving] = useState<BbLetter | null>(null);
  const [savingAll, setSavingAll] = useState(false);
  // Completion moment (slate #5): one transient strip when the last dirty
  // section saves; null when hidden.
  const [completionCount, setCompletionCount] = useState<number | null>(null);
  const completionTimerRef = useRef<number | null>(null);

  useSectionSaveCompletion(
    useCallback((count: number) => {
      setCompletionCount(count);
      if (completionTimerRef.current) window.clearTimeout(completionTimerRef.current);
      completionTimerRef.current = window.setTimeout(() => setCompletionCount(null), 4000);
    }, []),
  );

  // Frictionless buffers (slate #1): section switches never gate; the only
  // hard gate is tab close while any converted section holds unsaved edits.
  useEffect(() => {
    if (!anySectionDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anySectionDirty]);

  const saveSection = useCallback(
    async (letter: BbLetter) => {
      setSectionSaving(letter);
      try {
        const result = await saveRegistry.save(letter);
        // A failed Save must be loud (A.5a): the section hands back its own plain-words error.
        if (typeof result !== 'boolean' && !result.success && result.error) message.error(result.error);
      } finally {
        setSectionSaving(null);
      }
    },
    [saveRegistry],
  );

  const saveAllSections = useCallback(async () => {
    setSavingAll(true);
    try {
      const { failed } = await saveRegistry.saveAll();
      if (failed.length > 0) {
        message.error(`Could not save section${failed.length > 1 ? 's' : ''} ${failed.join(', ')} — they keep their edits.`);
      }
    } finally {
      setSavingAll(false);
    }
  }, [saveRegistry]);

  // The commit seam (§E prep blocker #3): a converted section's save() hands
  // its buffered FLAT fields back to the page, which merges them into the
  // draft and persists immediately through the page's own save path —
  // includeLetters re-admits the committing letter past the autosave-skip
  // filter. v2-homed fields never come through here.
  // Both read through refs updated EVERY render. The previous version was
  // memoized on [draft] and captured saveDraftNow from the last render where
  // the draft changed — on a v2-active tenant whose bb_identity fetch resolved
  // AFTER that render, the captured closure still held isV2Active=false and a
  // converted section's Save fired the legacy PUT, which 409s server-side for
  // v2 tenants ("tenant is on Brand Bible v2"). Caught live on §E, prod
  // 2026-08-17. Stale closures are this page's recurring failure class: never
  // let the seam capture state older than the click it services.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const saveDraftNowRef = useRef(saveDraftNow);
  saveDraftNowRef.current = saveDraftNow;
  const commitDraftFields = useCallback(
    async (fields: Record<string, unknown>, letter: BbLetter): Promise<boolean> => {
      // A section save started for another tenant (this closure is that render's) must not merge its fields
      // into the draft of the tenant shown now.
      if (hydratedTenantRef.current !== currentTenant) return false;
      const next = { ...draftRef.current, ...(fields as Partial<BrandBibleDraft>) };
      setDraft(next);
      return saveDraftNowRef.current(next, [letter]);
    },
    [currentTenant],
  );

  useEffect(() => {
    const focused = funnel.focused;
    if (!focused) return;
    const sid = sectionForEntry(focused.table, focused.entry);
    if (!sid) return;
    jumpToSection(sid);
    const node = sectionRefs.current[sid];
    if (node) {
      node.classList.add('bb-section-flash');
      window.setTimeout(() => node.classList.remove('bb-section-flash'), 1200);
    }
  }, [funnel.focused, jumpToSection]);

  // BB-DRAFTS-UX-01: every draft names its destination before you act on it, and
  // confirming/editing takes you there instead of the row silently vanishing.
  const sectionLabelFor = (row: DraftRow): string => {
    const sid = sectionForEntry(row.table, row.entry);
    const letter = (Object.keys(BB_LETTER_TO_SECTION) as BbLetter[]).find(
      (l) => BB_LETTER_TO_SECTION[l] === sid,
    );
    return letter ? `${letter} · ${BB_LETTER_LABEL[letter]}` : 'this section';
  };

  const goToDraftSection = (row: DraftRow) => {
    const sid = sectionForEntry(row.table, row.entry);
    if (sid) jumpToSection(sid);
  };

  const onDraftConfirmed = (row: DraftRow) => {
    const sid = sectionForEntry(row.table, row.entry);
    if (sid) {
      jumpToSection(sid);
      const el = sectionRefs.current[sid];
      if (el) {
        el.classList.add('bb-section-flash');
        setTimeout(() => el.classList.remove('bb-section-flash'), 1200);
      }
    }
    message.success(`Saved to ${sectionLabelFor(row)}`);
  };

  // The legacy (pre-v2) ring only knows the seven sections backed by flat-row
  // fields. Competitors/Channels/Performance have no flat-row equivalent, so
  // counting them would cap a fully-filled legacy bible at 70%.
  const legacySections = useMemo(() => SECTIONS.filter((s) => LEGACY_SCORED.has(s.id)), []);
  const completedCount = useMemo(
    () => legacySections.filter((s) => sectionStatus(s.id, draft, v2ServiceSectionContext)).length,
    [draft, legacySections, v2ServiceSectionContext],
  );
  const pct = Math.round((completedCount / legacySections.length) * 100);

  // A section save that was started for tenant A and finishes after a switch calls this closure of A's
  // render: it must not write A's values into B's draft.
  const draftIsForThisRender = () => hydratedTenantRef.current === currentTenant;
  const set = <K extends keyof BrandBibleDraft>(key: K, value: BrandBibleDraft[K]) => {
    if (!draftIsForThisRender()) return;
    setDraft((d) => ({ ...d, [key]: value }));
  };
  const setSocial = <K extends keyof SocialLinks>(key: K, value: string) => {
    if (!draftIsForThisRender()) return;
    setDraft((d) => ({ ...d, social: { ...d.social, [key]: value } }));
  };
  const setPersonas = (next: BuyerPersona[]) => set('buyer_personas', next);
  const addPersona = () =>
    setPersonas([
      ...draft.buyer_personas,
      { segment_name: '', demographics: '', pain_points: [], objections: [] },
    ]);

  // Competitors → blocklist mutations. Diff against existing blocklist on each call.
  const blocklistDomainSet = useMemo(() => new Set(blocklist.map((b) => b.domain)), [blocklist]);
  const setCompetitors = (next: string[]) => {
    set('competitors', next);
    if (!currentTenant) return;
    const nextSet = new Set(next);
    const toAdd = next.filter((d) => !blocklistDomainSet.has(d));
    const toRemove = blocklist.filter((b) => b.domain && !nextSet.has(b.domain));
    toAdd.forEach((domain) => {
      void addToPolicy
        .mutateAsync({ tenantId: currentTenant, domain, policyType: 'never_use' })
        .catch(() => {
          message.error(`Failed to add ${domain} to competitor exclusions`);
          setDraft((d) => ({
            ...d,
            competitors: d.competitors.filter((c) => c !== domain),
          }));
        });
    });
    toRemove.forEach((b) => {
      if (!b.id) return;
      void removeFromPolicy
        .mutateAsync({ entryId: b.id, tenantId: currentTenant })
        .catch(() => {
          message.error(`Failed to remove ${b.domain} from competitor exclusions`);
          setDraft((d) =>
            d.competitors.includes(b.domain)
              ? d
              : { ...d, competitors: [...d.competitors, b.domain] },
          );
        });
    });
  };

  const handleUseBrandBible = async () => {
    setUsingBrandBible(true);
    try {
      // "Use Brand Bible" leaves the page via router navigation, which
      // beforeunload does NOT cover — any converted section's buffered edits
      // would be silently dropped on unmount. This action means "commit
      // everything and go", so sweep every dirty section first and refuse to
      // navigate while any section still holds unsaved (failed) edits.
      const { failed } = await saveRegistry.saveAll();
      if (failed.length > 0) {
        message.error(
          `Save section${failed.length > 1 ? 's' : ''} ${failed.join(', ')} before continuing — their edits are not stored yet.`,
        );
        return;
      }
      // Same as autofill: an explicit whole-draft save — include converted
      // letters so their columns aren't silently dropped on the way out.
      const saved = await saveDraftNow(draft, [...CONVERTED_SECTIONS]);
      if (saved) {
        navigate('/content/planner');
      }
    } finally {
      setUsingBrandBible(false);
    }
  };

  if (!currentTenant) {
    return (
      <div className="grid place-items-center min-h-[60vh] text-muted-foreground">
        <div className="text-center">
          <BookOpen className="size-10 mx-auto mb-3 text-coral" />
          <div className="text-base font-semibold text-foreground mb-1">Pick a workspace first</div>
          <div className="text-sm">Select one from the switcher above.</div>
        </div>
      </div>
    );
  }

  // A failed flat GET (anything but a 404, which the service maps to "no row") shows an error,
  // never the previous tenant's draft.
  const v2LoadFailed = v2InitFailedFor === currentTenant;
  if (flatLoadFailed || v2LoadFailed) {
    return (
      <div data-testid="brand-bible-load-error" className="grid place-items-center min-h-[60vh] text-muted-foreground">
        <div className="text-center">
          <div className="text-base font-semibold text-foreground mb-1">Couldn't load this Brand Bible</div>
          <div className="text-sm mb-3">Nothing was changed. Check your connection and try again.</div>
          <button
            type="button"
            onClick={() => {
              if (v2LoadFailed) {
                setV2InitFailedFor(null);
                void initV2(currentTenant).catch(() => setV2InitFailedFor(currentTenant));
              }
              if (flatLoadFailed) void refetchFlat?.();
            }}
            className="px-3.5 py-2 rounded-lg bg-card border border-border text-foreground text-[13.5px] font-medium hover:bg-warm-bg"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
  // Until this tenant's flat row is hydrated into the draft and its v2 load has started/settled, no section
  // (and no banner) renders: no input can show, or edit, the previous tenant's values.
  // The store must be on THIS tenant and have loaded it: a store on another tenant (or holding another
  // tenant's entries) never renders here.
  const v2StillLoadingForCurrent =
    v2InitFor !== currentTenant || !(storeTenantId === currentTenant && v2LoadedTenantId === currentTenant);
  if (hydratedFor !== currentTenant || v2StillLoadingForCurrent) {
    return (
      <div data-testid="brand-bible-loading" aria-busy="true" className="flex flex-col gap-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const activeMeta = SECTIONS.find((s) => s.id === active) ?? SECTIONS[0];

  return (
    <BbDraftCommitProvider value={commitDraftFields}>
    <div className="grid grid-cols-[208px_1fr] -m-6 h-[calc(100vh-8rem)] overflow-hidden bg-warm-bg rounded-xl">
      {/* ============ Sub-nav (200px) ============ */}
      <BrandBibleSubNav
        tenantLabel={tenantInfo?.website || currentTenant}
        v2Score={v2Score}
        v2Delta={v2Delta}
        dropRemaining={dropReview.remaining}
        attentionTotal={toVerifyRows.length + draftRows.length}
        pct={pct}
        completedCount={completedCount}
        legacyCount={legacySections.length}
        v2Rollups={v2Rollups}
        draft={draft}
        v2ServiceSectionContext={v2ServiceSectionContext}
        active={active}
        v2Attention={v2Attention}
        onSelectLetter={setActiveLetter}
        dirtyLetters={dirtyLetters}
        onJump={jumpToSection}
        onSaveAll={saveAllSections}
        savingAll={savingAll}
        isSaving={updateMutation.isPending || createMutation.isPending}
        isLoading={isLoading}
        savedAt={savedAt}
      />

      {/* ============ Main scroller ============ */}
      <main ref={scrollerRef} className="overflow-auto relative">
        <div className="sticky top-0 z-10 bg-warm-bg/90 backdrop-blur-md border-b border-border">
          <div className="px-8 py-3 flex items-center justify-between gap-4">
            <div>
              <div className="text-[11px] text-muted-foreground font-semibold tracking-wider uppercase">
                {activeMeta.hint}
              </div>
              <div className="text-base font-semibold mt-0.5">{activeMeta.title}</div>
            </div>
            <div className="flex items-center gap-2.5">
              {/* Explicit-save controls (save-ux slate #3/#4): only a CONVERTED
                  section registers, so these render for converted letters and
                  nothing else changes. Discard is a quiet text action, never a
                  visual peer of Save. */}
              {activeSectionRegistered && (
                <>
                  {activeSectionChanges > 0 && (
                    <button
                      type="button"
                      data-testid="bb-discard-section"
                      onClick={() => saveRegistry.discard(activeLetter)}
                      className="text-[12.5px] text-muted-foreground hover:text-foreground underline underline-offset-2 cursor-pointer"
                    >
                      Discard {activeSectionChanges} edit{activeSectionChanges === 1 ? '' : 's'}
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="bb-save-section"
                    disabled={activeSectionChanges === 0 || sectionSaving === activeLetter || gatherRunning}
                    onClick={() => saveSection(activeLetter)}
                    className={cn(
                      'inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-[13.5px] font-semibold border cursor-pointer',
                      activeSectionChanges > 0
                        ? 'bg-coral hover:bg-coral-hover border-coral text-white'
                        : 'bg-card border-border text-muted-foreground cursor-default',
                    )}
                  >
                    {sectionSaving === activeLetter && <Loader2 className="size-3.5 animate-spin" />}
                    Save {activeLetter}
                    {activeSectionChanges > 0 &&
                      ` · ${activeSectionChanges} change${activeSectionChanges === 1 ? '' : 's'}`}
                  </button>
                  {gatherRunning && (
                    <span data-testid="bb-save-held-reason" className="text-[12px] text-muted-foreground">
                      Saving is paused while the website is being read.
                    </span>
                  )}
                </>
              )}
              {FEATURE_FLAGS.brandBibleFileIngestion && (
                <button
                  type="button"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-card border border-border text-foreground text-[13.5px] font-medium hover:bg-warm-bg cursor-pointer"
                  onClick={() => scrollerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <Upload className="size-3.5" />
                  Upload documents
                </button>
              )}
            </div>
          </div>
          {FEATURE_FLAGS.brandBibleFileIngestion && (
            <VerifyFunnelBar
              funnel={funnel}
              scope="drop"
              onDone={dropReview.remaining === 0 ? dropReview.clearFilter : undefined}
            />
          )}
        </div>

        {completionCount !== null && (
          // Completion moment (save-ux slate #5): fires only on a save-driven
          // "last dirty section cleared" transition — never on discard.
          <div
            data-testid="bb-completion-moment"
            className="sticky top-[57px] z-10 mx-8 mt-3 border border-[hsl(var(--status-success)/0.3)] rounded-xl bg-[hsl(var(--status-success-bg))] px-4 py-2.5 flex items-center gap-3 text-[13.5px]"
          >
            <span className="grid place-items-center w-5 h-5 rounded-full bg-[hsl(var(--status-success))] text-white flex-shrink-0">
              <Check className="size-3" />
            </span>
            <span className="flex-1">
              Brand Bible updated — {completionCount} section{completionCount === 1 ? '' : 's'} saved
            </span>
            <button
              type="button"
              onClick={() => setCompletionCount(null)}
              className="grid place-items-center w-6 h-6 rounded text-muted-foreground hover:text-foreground cursor-pointer"
              title="Dismiss"
            >
              <X className="size-3.5" />
            </button>
          </div>
        )}

        <div className="w-full px-6 pt-6 pb-32">
          {FEATURE_FLAGS.brandBibleFileIngestion && currentTenant && (
            <BbFileDropCard
              key={currentTenant}
              tenantId={currentTenant}
              onJumpToDrafts={(fileId) => startDropReview([fileId])}
              onReviewDrafts={() => startDropReview()}
              dropRemaining={dropReview.remaining}
              dropByLetter={dropReview.byLetter}
            />
          )}
          {!v2Loading && (
            <NeedsAttentionQueue
              toVerify={toVerifyRows}
              drafts={draftRows}
              gaps={v2Score?.gaps ?? []}
              onJump={jumpToSection}
              siteLabel={tenantInfo?.website || currentTenant || 'the client site'}
              provenance={v2Provenance}
            />
          )}
          {/* Web gather: drafts from the client's site + GBP (flag, role and allowlist gated inside) */}
          <div className="mb-6 flex flex-col gap-3 empty:hidden">
            <GatherBanner
              tenantId={currentTenant}
              siteLabel={tenantInfo?.website || currentTenant || 'the client site'}
              canEdit={canTryGather}
            />
            {/* Client questionnaire: a sheet the client fills in, imported as drafts (flag gated on the backend) */}
            <QuestionnairePanel tenantId={currentTenant} canEdit={canTryGather} />
          </div>

          {/* ============ A · Identity Card ============ */}
          {/* Mount swap (feat/bb-save §A part 1): the inline §A block is now
              <SectionEssentials/> (the dormant-on-main tested extraction), a
              strict superset of the inline block — it recovers the row-level
              verify pills, "Verify all", the licences/insurance rows, and the
              Primary-location details box (radius/tracking_phone/gbp_url/hours).
              The page still owns all state/callbacks and passes them down; the
              visibility wrapper carries the sectionVisibilityProps main uses to
              toggle/scroll each section (mirrors §E/SectionAudience). Autosave
              for §A is unchanged — this PR does NOT flip CONVERTED_SECTIONS. */}
          <div {...sectionVisibilityProps(active, 'essentials')}>
            <SectionEssentials
              sectionRef={(el) => {
                sectionRefs.current.essentials = el;
              }}
              draft={draft}
              set={set}
              draftRowsForTables={draftRowsForTables}
              tenantInfo={tenantInfo}
              currentTenant={currentTenant}
              canVerifyV2={canVerifyV2}
              goToDraftSection={goToDraftSection}
              onDraftConfirmed={onDraftConfirmed}
              sectionLabelFor={sectionLabelFor}
              businessNameDup={businessNameDup}
              verifyV2={verifyV2}
              identity={identity}
              identityIsDraft={identityIsDraft}
              verifyIdentity={verifyIdentity}
              saveIdentity={saveIdentity}
              saveOwner={saveOwner}
              identitySaveErrorToast={identitySaveErrorToast}
              socialsMap={socialsMap}
              socialDraft={socialDraft}
              setSocialDraft={setSocialDraft}
              addSocial={addSocial}
              removeSocial={removeSocial}
              syncServiceLocationsV2={syncServiceLocationsV2}
              primaryLocation={primaryLocation}
              savePrimaryLocation={savePrimaryLocation}
            />
          </div>

          {/* ============ B · Voice Profile ============ */}
          <section
            {...sectionVisibilityProps(active, 'voice')}
            ref={(el) => {
              sectionRefs.current.voice = el;
            }}
            className="bg-card border border-border rounded-2xl px-6 py-5 mb-4 shadow-sm"
          >
            <SectionHeader
              id="voice"
              right={
                <AISuggest
                  sectionId="voice"
                  placeholder="Suggest tone"
                  source="from your overview"
                  kind="text"
                  existing={[draft.tone_of_writing]}
                  getPrompt={() =>
                    `Based on this business: ${draft.description || ''}. Suggest 2 distinct tone-of-voice descriptions (15-25 words each) for their content. Return JSON array of strings.`
                  }
                  onAcceptText={(t) => set('tone_of_writing', t)}
                />
              }
            />
            {/* §B spec-model: voice_summary → attributes → mechanics → do/never-say →
                formatting rules → golden examples → rejected examples (all one section,
                nothing collapsed behind an Expand). The wall below is scoped to
                chunk-shaped drafts only (kind-based rows with no single field to carry
                a chip) — voice_summary's own draft surfaces inline as the field-level
                "found on site · Verify" chip inside VoiceProfilePanel, same pattern §A
                uses for a duplicated legal_name, so it is excluded here to avoid
                double-exposure of the same discovery. */}
            {voiceChunkDrafts.length > 0 && (
              <div className="mb-4">
                <EnrichmentDraftsPanel
                  tenantId={currentTenant}
                  drafts={voiceChunkDrafts}
                  siteLabel={tenantInfo?.website || currentTenant || 'the client site'}
                  canVerify={canVerifyV2}
                  onEdit={goToDraftSection}
                  onConfirmed={onDraftConfirmed}
                  sectionLabelFor={sectionLabelFor}
                />
              </div>
            )}
            <VoiceProfilePanel canVerify={canVerifyV2} legacyToneFallback={draft.tone_of_writing} />

            {/* Legacy §B fields — ALIASED, not replaced (Carl addendum, overrides the
                earlier "collapse and reconcile" pass, same pattern as the theme_config
                alias: primary_colors/secondary_colors/typography stay the canonical,
                directly-editable fields below, and theme_config is a derived
                projection from them on save, never the other way around). Mission /
                Value proposition / Tone of voice / Primary CTA keep their own
                unconditional `set()` write path here — same BrandBibleUpdate flow as
                before this redesign, working identically for tenants with no bb_ v2
                data at all. Rendered open (not collapsed) so they're visibly present,
                not just reachable — a reviewer checks that these still render. The
                only new coupling is one-way, read-only: `legacyToneFallback` seeds
                VoiceProfilePanel's summary textarea for DISPLAY when voice_summary is
                still empty; editing the new summary never writes back here. */}
            <div className="col-span-2 mt-3.5 rounded-lg border border-dashed border-input bg-warm-bg">
              <button
                type="button"
                onClick={() => setShowVoiceLegacy((v) => !v)}
                className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left"
              >
                {showVoiceLegacy ? (
                  <ChevronDown className="size-3.5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="size-3.5 text-muted-foreground" />
                )}
                <span className="text-[12.5px] font-semibold">Legacy fields</span>
                <span className="text-[11px] text-muted-foreground">
                  mission · value proposition · tone of voice · primary CTA
                </span>
              </button>
              {showVoiceLegacy && (
                <div className="grid grid-cols-2 gap-3.5 border-t border-border p-3.5">
                  <div>
                    <FieldLabel required review={reviewChip('company_mission', draft.company_mission)}>Mission</FieldLabel>
                    <TextArea
                      value={draft.company_mission}
                      onChange={(v) => set('company_mission', v)}
                      rows={2}
                    />
                  </div>
                  <div>
                    <FieldLabel required review={reviewChip('value_proposition', draft.value_proposition)}>Value proposition</FieldLabel>
                    <TextArea
                      value={draft.value_proposition}
                      onChange={(v) => set('value_proposition', v)}
                      rows={2}
                    />
                  </div>
                  <div>
                    <FieldLabel
                      required
                      hint={isV2Active ? 'derived from Voice summary above' : undefined}
                      review={reviewChip('tone_of_writing', draft.tone_of_writing)}
                    >
                      Tone of voice
                    </FieldLabel>
                    {isV2Active ? (
                      // §7.7: read-only derived mirror of bb_voice_profile.voice_summary
                      // — NOT an editable control. Editing lives in the Voice summary
                      // field inside VoiceProfilePanel above; this box only reflects it,
                      // so it can never again look editable while silently discarding
                      // input (the decoy bug this replaces).
                      <div
                        data-testid="tone-of-writing-readonly-mirror"
                        className="min-h-[52px] px-3 py-2.5 rounded-lg bg-teal-light/40 border border-dashed border-input text-[13px] text-muted-foreground"
                      >
                        {voiceProfileForToneMirror.entry?.voice_summary || 'Set in Voice summary above.'}
                      </div>
                    ) : (
                      <TextArea
                        value={draft.tone_of_writing}
                        onChange={(v) => set('tone_of_writing', v)}
                        rows={2}
                        placeholder="e.g. Friendly, expert, direct. Active voice."
                      />
                    )}
                  </div>
                  <div>
                    <FieldLabel review={reviewChip('call_to_action', draft.call_to_action)}>Primary call to action</FieldLabel>
                    <TextField
                      value={draft.call_to_action}
                      onChange={(v) => set('call_to_action', v)}
                      placeholder="e.g. Schedule your free estimate today"
                    />
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ============ C · Offers & Pricing Facts ============ */}
          <section
            {...sectionVisibilityProps(active, 'service')}
            ref={(el) => {
              sectionRefs.current.service = el;
            }}
            className="bg-card border border-border rounded-2xl px-6 py-5 mb-4 shadow-sm"
          >
            <SectionHeader
              id="service"
              right={
                <AISuggest
                  sectionId="service"
                  placeholder="Suggest services"
                  source="based on your industry"
                  existing={draft.services_offered}
                  getPrompt={({ existing, dismissed }) =>
                    `For ${draft.business_name || 'this business'} (${draft.description || ''}), suggest 6 additional services they likely offer. Already have: ${existing.join(', ') || 'none'}. Avoid: ${dismissed.join(', ') || 'none'}. Return as a JSON array of short service names only.`
                  }
                  onAccept={(s) => set('services_offered', [...draft.services_offered, s])}
                />
              }
            />
            {draftRowsForTables(['services', 'locations', 'offers']).length > 0 && (
              <div className="mb-4">
                <EnrichmentDraftsPanel
                  tenantId={currentTenant}
                  drafts={draftRowsForTables(['services', 'locations', 'offers'])}
                  siteLabel={tenantInfo?.website || currentTenant || 'the client site'}
                  canVerify={canVerifyV2}
                  onEdit={goToDraftSection}
                  onConfirmed={onDraftConfirmed}
                  sectionLabelFor={sectionLabelFor}
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3.5">
              <div className="col-span-2">
                <FieldLabel
                  required
                  hint={`${draft.services_offered.length} services · drag to reorder · dot = price policy`}
                >
                  Services offered
                </FieldLabel>
                <ServicesPicker
                  value={draft.services_offered}
                  onChange={(v) => {
                    set('services_offered', v);
                    void syncServicesOfferedV2(v);
                  }}
                  primaryCount={3}
                  confirmRemove={(name) =>
                    isConfirmedGathered(findServiceEntry(name)) ? confirmRemoveGathered(name) : Promise.resolve(true)
                  }
                  chipDot={(name) => {
                    const entry = findServiceEntry(name);
                    return entry?.price_policy ?? (entry ? 'unset' : null);
                  }}
                  selectedChip={selectedServiceChip}
                  onSelectChip={setSelectedServiceChip}
                />
                {/* Gap 4a: aggregate price-policy nudge — the per-chip dot shows which
                    service is missing a policy, this says how many are left. */}
                {servicePriceGap.total > 0 && (
                  <div data-testid="price-policy-gap-meter" className="mt-1.5 text-[11px]">
                    <span
                      className={cn(
                        'font-medium tabular-nums',
                        servicePriceGap.missing > 0 ? 'text-coral' : 'text-muted-foreground',
                      )}
                    >
                      {servicePriceGap.withPolicy} of {servicePriceGap.total}
                    </span>
                    <span className="text-muted-foreground"> have a price policy</span>
                    {servicePriceGap.missing > 0 && (
                      <span className="font-medium text-[hsl(var(--status-warning))]">
                        {' '}
                        · {servicePriceGap.missing} need a price policy to launch
                      </span>
                    )}
                  </div>
                )}
                {selectedServiceChip && (
                  <ServiceChipDetailRow
                    // Re-key per service so switching chips remounts the row —
                    // its uncontrolled defaultValue inputs otherwise keep the
                    // previous service's typed text (cross-service leak).
                    key={selectedServiceChip}
                    serviceName={selectedServiceChip}
                    entry={findServiceEntry(selectedServiceChip)}
                    canVerify={canVerifyV2}
                    saving={v2Saving}
                    onSave={(patch) =>
                      void upsertV2(
                        'services',
                        { name: selectedServiceChip, ...patch },
                        findServiceEntry(selectedServiceChip)?.id,
                      )
                    }
                    onVerify={() => {
                      const entry = findServiceEntry(selectedServiceChip);
                      if (entry) void verifyV2('services', entry.id);
                    }}
                  />
                )}
              </div>
              {/* §C Offers & pricing facts — ported from the wizard (design spec hard
                  constraint: capability must stay reachable now that /brand-bible/wizard
                  redirects here). */}
              <OffersPanel canVerify={canVerifyV2} />
            </div>
          </section>

          {/* ============ D · Proof Library ============ */}
          <section
            {...sectionVisibilityProps(active, 'proof')}
            ref={(el) => {
              sectionRefs.current.proof = el;
            }}
            className="bg-card border border-border rounded-2xl px-6 py-5 mb-4 shadow-sm"
          >
            <SectionHeader
              id="proof"
              right={
                <AISuggest
                  sectionId="proof"
                  placeholder="Suggest credentials"
                  source="typical for your field"
                  kind="text"
                  existing={[draft.credentials_licenses]}
                  getPrompt={() =>
                    `For ${draft.business_name || 'this business'} (${draft.description || ''}), suggest 2 credentials/licenses statements they likely qualify for (40-60 words). Return JSON array of strings.`
                  }
                  onAcceptText={(t) => set('credentials_licenses', t)}
                />
              }
            />
            {draftRowsForTables(['review_stats', 'case_studies']).length > 0 && (
              <div className="mb-4">
                <EnrichmentDraftsPanel
                  tenantId={currentTenant}
                  drafts={draftRowsForTables(['review_stats', 'case_studies'])}
                  siteLabel={tenantInfo?.website || currentTenant || 'the client site'}
                  canVerify={canVerifyV2}
                  onEdit={goToDraftSection}
                  onConfirmed={onDraftConfirmed}
                  sectionLabelFor={sectionLabelFor}
                />
              </div>
            )}
            <ProofLibraryPanel canVerify={canVerifyV2} legacy={{ credentialsLicenses: draft.credentials_licenses, caseStudies: draft.case_studies }} />
            <div className="grid grid-cols-2 gap-3.5">
              {/* Legacy free-text proof editors — v1 ONLY. For a v2-active tenant
                  (Boolean(identity.entry)) these two fields have typed homes in
                  bb_credentials / bb_case_studies: LEGACY_ONLY_SCALAR_KEYS excludes
                  them and the legacy flat-row write path 409s, so anything typed
                  here would be silently discarded. Their pre-v2 text is not lost —
                  ProofLibraryPanel's read-only LegacyReference (fed the same draft
                  via the legacy={{…}} prop above) still surfaces it. A v1 tenant
                  keeps these editors exactly as before. Gating idiom mirrors the
                  Tone-of-voice §7.7 block above. */}
              {!isV2Active && (
                <>
                  <div>
                    <FieldLabel>Credentials & licenses</FieldLabel>
                    <TextArea
                      value={draft.credentials_licenses}
                      onChange={(v) => set('credentials_licenses', v)}
                      rows={3}
                      placeholder="e.g. Master Plumber #MP-12345, BBB A+ Rating"
                    />
                  </div>
                  <div>
                    <FieldLabel>Case studies</FieldLabel>
                    <TextArea
                      value={draft.case_studies}
                      onChange={(v) => set('case_studies', v)}
                      rows={3}
                      placeholder="One-line stories with concrete results"
                    />
                  </div>
                </>
              )}
              <div className="col-span-2">
                <FieldLabel>Social profiles</FieldLabel>
                <div className="grid grid-cols-2 gap-2.5">
                  {([
                    { k: 'facebook', label: 'Facebook' },
                    { k: 'instagram', label: 'Instagram' },
                    { k: 'linkedin', label: 'LinkedIn' },
                    { k: 'twitter', label: 'Twitter / X' },
                    { k: 'youtube', label: 'YouTube' },
                  ] as const).map((s) => (
                    <div key={s.k} className="flex items-center gap-2 min-w-0">
                      <div className="w-[72px] text-[12.5px] text-muted-foreground flex-shrink-0">
                        {s.label}
                      </div>
                      <div className="flex-1 min-w-0">
                        <TextField
                          value={draft.social[s.k]}
                          onChange={(v) => setSocial(s.k, v)}
                          placeholder={`https://${s.k}.com/…`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
          {/* ============ E · Market & Customer ============ */}
          {/* restored from fold-in park 55822ae6f for §E Market Notes (recover/bb-e-foldin):
              inline §E audience fields replaced by SectionAudience (grounded AI Suggest)
              + the Market-notes verify list. Visibility wrapper carries the
              sectionVisibilityProps main uses to toggle/scroll each section. */}
          <div {...sectionVisibilityProps(active, 'audience')}>
            <SectionAudience
              sectionRef={(el) => {
                sectionRefs.current.audience = el;
              }}
              draft={draft}
              set={set}
              addPersona={addPersona}
              setPersonas={setPersonas}
              currentTenant={currentTenant}
              icpNotes={v2ChunksEEntries
                .filter((e) => e.kind === 'icp_note')
                .map((e) => (e.body ?? '').trim())
                .filter(Boolean)}
              seasonalityNotes={v2ChunksEEntries
                .filter((e) => e.kind === 'seasonality_note')
                .map((e) => (e.body ?? '').trim())
                .filter(Boolean)}
              onAcceptMarketNote={(kind, value) => {
                const body = value.trim();
                if (!body) return;
                void upsertV2('chunks', { kind, section: 'E', body }).catch((e: unknown) =>
                  message.error(
                    `Failed to save ${kind === 'icp_note' ? 'ICP' : 'seasonality'} note — ${
                      e instanceof Error ? e.message : 'request failed'
                    }`,
                  ),
                );
              }}
            />
            {v2ChunksEEntries.length > 0 && (
              <section className="bg-card border border-border rounded-2xl px-6 py-5 mb-4 shadow-sm">
                <ChunkVerifyListBody
                  title="Market notes — counted by the score"
                  entries={v2ChunksEEntries}
                  canVerify={canVerifyV2}
                  onVerify={(id) => verifyV2('chunks', id)}
                  onUnverify={(id) => unverifyV2('chunks', id)}
                  // Editing a market note is a human assertion, so saving verifies
                  // it (design "1b — by the book"): PATCH the body first, THEN verify
                  // (no extra verify args — never publish). The provenance `source`
                  // stamp is deliberately out of scope here (not PATCH-writable).
                  onEditBody={async (id, body) => {
                    await upsertV2('chunks', { body }, id);
                    await verifyV2('chunks', id);
                  }}
                  // §E cluster Verify-all stays enabled, no suffix (matches the
                  // shipped "✓ Verify all · N" copy — the primitive's default
                  // suffix is "pending", which §E never had).
                  verifyAll={{ enabled: true, suffix: '' }}
                />
              </section>
            )}
          </div>

          <section
            {...sectionVisibilityProps(active, 'competitors')}
            ref={(el) => {
              sectionRefs.current.competitors = el;
            }}
            className="bg-card border border-border rounded-2xl px-6 py-5 mb-4 shadow-sm"
          >
            <SectionHeader id="competitors" />
            <p className="mb-3 text-[12px] text-muted-foreground">
              Internal only — the spec's default is that agents never name a competitor in public copy.
            </p>
            {/* Canonical store: bb_chunks section='F' — the competitive intelligence the
                score and attention queue count. Persisted mode: the component's own hook
                owns read/write; no value/onChange from the page. */}
            <p className="mb-2 text-[12px] font-semibold text-muted-foreground">
              Canonical competitive set
            </p>
            <CompetitiveSetSection tenantDomain={currentTenant ?? undefined} />
            {/* Curated store: tenant_blocklist never_use — link policy, NOT competitor
                identity. LEGACY-ONLY surface (official 9227 L3 finding, amended by
                ARCH review of #1621): for v2-active tenants the typed panel above is
                authoritative, so this editor hides — but ONLY when the blocklist is
                EMPTY. Live v2-active tenants with populated blocklists exist in prod;
                hiding their only operator surface over live rows would strand that
                data until reconcile/Stage-2 owns the migration. Pre-v2 tenants keep
                the surface unchanged. Blocklist plumbing (draft.competitors /
                setCompetitors / useLinkPolicy) stays intact throughout — generation-
                time link exclusion reads the table, not this UI. */}
            {!(isV2Active && blocklist.length === 0) && (
              <>
                <p className="mt-4 mb-2 text-[12px] font-semibold text-muted-foreground">
                  Curated link-block list (never linked — separate from competitors)
                </p>
                <div className="grid grid-cols-2 gap-3.5">
                  <div className="col-span-2">
                    <FieldLabel review={reviewChip('competitors', draft.competitors)}>Never-link list</FieldLabel>
                    <CompetitorsInput value={draft.competitors} onChange={setCompetitors} />
                  </div>
                </div>
              </>
            )}
          </section>

          {/* ============ G · Compliance & Never List ============ */}
          <section
            {...sectionVisibilityProps(active, 'guardrails')}
            ref={(el) => {
              sectionRefs.current.guardrails = el;
            }}
            className="bg-card border border-border rounded-2xl px-6 py-5 mb-4 shadow-sm"
          >
            <SectionHeader
              id="guardrails"
              right={
                <AISuggest
                  sectionId="guardrails"
                  placeholder="Suggest words to avoid"
                  source="common pitfalls"
                  existing={draft.words_avoid}
                  getPrompt={({ existing, dismissed }) =>
                    `For ${draft.business_name || 'this business'} with tone: "${draft.tone_of_writing || 'professional'}", suggest 6 words/phrases to avoid in their content. Already excluded: ${existing.join(', ') || 'none'}. Avoid suggesting: ${dismissed.join(', ') || 'none'}. Return JSON array of short strings.`
                  }
                  onAccept={(s) => set('words_avoid', [...draft.words_avoid, s])}
                />
              }
            />
            {draftRowsForTables(['compliance_rules']).length > 0 && (
              <div className="mb-4">
                <EnrichmentDraftsPanel
                  tenantId={currentTenant}
                  drafts={draftRowsForTables(['compliance_rules'])}
                  siteLabel={tenantInfo?.website || currentTenant || 'the client site'}
                  canVerify={canVerifyV2}
                  onEdit={goToDraftSection}
                  onConfirmed={onDraftConfirmed}
                  sectionLabelFor={sectionLabelFor}
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3.5">
              <div>
                <FieldLabel hint="Never appear in generated copy">Words to avoid</FieldLabel>
                <SmartTagInput
                  value={draft.words_avoid}
                  onChange={(v) => set('words_avoid', v)}
                  placeholder="e.g. cheap, hack, guys"
                  suggestions={SUGG_WORDS_AVOID}
                  accent="warning"
                />
              </div>
              <div>
                <FieldLabel hint="Often confused with what you do">Services NOT offered</FieldLabel>
                <SmartTagInput
                  value={draft.services_not_offered}
                  onChange={(v) => set('services_not_offered', v)}
                  placeholder="e.g. Septic systems"
                  suggestions={SUGG_SERVICES_NOT}
                  accent="warning"
                />
              </div>
              <div className="col-span-2">
                <FieldLabel hint="Compliance, legal, industry rules">Regulatory notes</FieldLabel>
                <TextArea
                  value={draft.regulatory_notes}
                  onChange={(v) => set('regulatory_notes', v)}
                  rows={2}
                  placeholder="e.g. Must include license number on all service pages."
                />
              </div>
            </div>
            <CompliancePanel canVerify={canVerifyV2} legacy={{ servicesNotOffered: draft.services_not_offered, regulatoryNotes: draft.regulatory_notes, wordsAvoid: draft.words_avoid }} />
          </section>

          {/* ============ H · Visual Identity ============ */}
          <section
            {...sectionVisibilityProps(active, 'visual')}
            ref={(el) => {
              sectionRefs.current.visual = el;
            }}
            className="bg-card border border-border rounded-2xl px-6 py-5 mb-4 shadow-sm"
          >
            <SectionHeader
              id="visual"
              right={
                <AISuggest
                  sectionId="visual"
                  placeholder="Suggest imagery"
                  source="matching your brand"
                  kind="text"
                  existing={[draft.visual_identity]}
                  getPrompt={() =>
                    `For ${draft.business_name || 'this business'} (${draft.description || ''}), suggest 2 imagery direction descriptions (25-40 words each). Return JSON array of strings.`
                  }
                  onAcceptText={(t) => set('visual_identity', t)}
                />
              }
            />
            {draftRowsForTables(['visual_identity']).length > 0 && (
              <div className="mb-4">
                <EnrichmentDraftsPanel
                  tenantId={currentTenant}
                  drafts={draftRowsForTables(['visual_identity'])}
                  siteLabel={tenantInfo?.website || currentTenant || 'the client site'}
                  canVerify={canVerifyV2}
                  onEdit={goToDraftSection}
                  onConfirmed={onDraftConfirmed}
                  sectionLabelFor={sectionLabelFor}
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel hint="Used in headers & buttons">Primary colors</FieldLabel>
                <div className="flex flex-wrap gap-1.5">
                  {draft.primary_colors.map((c, i) => (
                    <ColorChip
                      key={i}
                      value={c}
                      onChange={(cc) => {
                        const a = [...draft.primary_colors];
                        a[i] = cc;
                        set('primary_colors', a);
                      }}
                      onRemove={() => {
                        const a = [...draft.primary_colors];
                        a.splice(i, 1);
                        set('primary_colors', a);
                      }}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      set('primary_colors', [...draft.primary_colors, { hex: '#111111', label: '' }])
                    }
                    className="px-2.5 py-1 border border-dashed border-border bg-transparent rounded-lg text-muted-foreground text-[12.5px] cursor-pointer flex items-center gap-1.5 hover:text-foreground hover:border-coral"
                  >
                    <Plus className="size-3" /> Add color
                  </button>
                </div>
              </div>
              <div>
                <FieldLabel>Secondary / accents</FieldLabel>
                <div className="flex flex-wrap gap-1.5">
                  {draft.secondary_colors.map((c, i) => (
                    <ColorChip
                      key={i}
                      value={c}
                      onChange={(cc) => {
                        const a = [...draft.secondary_colors];
                        a[i] = cc;
                        set('secondary_colors', a);
                      }}
                      onRemove={() => {
                        const a = [...draft.secondary_colors];
                        a.splice(i, 1);
                        set('secondary_colors', a);
                      }}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      set('secondary_colors', [...draft.secondary_colors, { hex: '#0A76A8', label: '' }])
                    }
                    className="px-2.5 py-1 border border-dashed border-border bg-transparent rounded-lg text-muted-foreground text-[12.5px] cursor-pointer flex items-center gap-1.5 hover:text-foreground hover:border-coral"
                  >
                    <Plus className="size-3" /> Add color
                  </button>
                </div>
              </div>
              <BrandAccentPreview
                primary={stringifyColors(draft.primary_colors)}
                secondary={stringifyColors(draft.secondary_colors)}
              />
              <div>
                <FieldLabel>Typography</FieldLabel>
                <TextField
                  value={draft.typography}
                  onChange={(v) => set('typography', v)}
                  placeholder="Headings: Inter Bold · Body: Roboto"
                />
              </div>
              <div className="col-span-2">
                <FieldLabel>Imagery & references</FieldLabel>
                <TextArea
                  value={draft.visual_identity}
                  onChange={(v) => set('visual_identity', v)}
                  rows={2}
                  placeholder="Photo style, lighting, mood…"
                />
              </div>
            </div>
            <div className="mt-4">
              <ImageSourceSettings website={currentTenant ?? null} />
            </div>
            <VisualIdentityPanel canVerify={canVerifyV2} />
          </section>

          <section
            {...sectionVisibilityProps(active, 'channels')}
            ref={(el) => {
              sectionRefs.current.channels = el;
            }}
            className="bg-card border border-border rounded-2xl px-6 py-5 mb-4 shadow-sm"
          >
            <SectionHeader id="channels" />
            <div className="grid grid-cols-2 gap-3.5">
              <ChannelRulesPanel canVerify={canVerifyV2} />
            </div>
          </section>

          <BrandBibleSectionPager active={activeLetter} onSelect={setActiveLetter} />

          {/* Footer */}
          <div className="mt-2 pt-4 border-t border-border flex items-center justify-between gap-4">
            <div className="text-[12px] text-muted-foreground">
              {pct < 100 ? (
                <>
                  Brand bible is <strong className="text-foreground">{pct}% complete</strong>. AI agents
                  will use what's filled in — keep going when ready.
                </>
              ) : (
                <>Brand bible complete · all sections ready.</>
              )}
            </div>
            <button
              type="button"
              onClick={handleUseBrandBible}
              disabled={usingBrandBible}
              className={cn(
                'inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-coral text-white text-[13.5px] font-semibold cursor-pointer',
                usingBrandBible ? 'bg-coral/60 border-coral/60 cursor-wait' : 'bg-coral hover:bg-coral-hover',
              )}
            >
              {usingBrandBible ? <Loader2 className="size-3.5 animate-spin" /> : <Zap className="size-3.5" />}
              {usingBrandBible ? 'Saving…' : 'Use brand bible'}
              {!usingBrandBible && <ChevronRight className="size-3.5" />}
            </button>
          </div>
        </div>
      </main>
    </div>
    </BbDraftCommitProvider>
  );
};

export default BrandBiblePage;
